import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ToolSidebar from '../../utils/ToolSideBar';
import { approveRequest, assignApproval, declineRequest, fetchApproval, requestMoreInfo, respondToInfoRequest, updateFulfillment } from '../../../services/approvalApi';
import '../../../css/superuser/StaffDirectoryPage.css';
import '../../../css/staff/InternalWorkspace.css';

function DetailMetric({ label, value, children }) {
  return <div className="team-detail-metric"><span>{label}</span><strong>{children || value || 'None'}</strong></div>;
}

function ActionForm({ title, label, placeholder, required, onSubmit }) {
  const [text, setText] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  async function handleSubmit(event) {
    event.preventDefault();
    try {
      await onSubmit(text);
      setText('');
      setMessage('Saved.');
      setError('');
    } catch (err) {
      setError(err.message || 'Action failed.');
      setMessage('');
    }
  }
  return (
    <form className="team-action-form" onSubmit={handleSubmit}>
      <h3>{title}</h3>
      <label><span>{label}</span><textarea value={text} onChange={(event) => setText(event.target.value)} placeholder={placeholder} required={required} /></label>
      <button type="submit">{title}</button>
      {(message || error) && <p className={error ? 'team-form-message is-error' : 'team-form-message'}>{error || message}</p>}
    </form>
  );
}

function AssignmentForm({ approval, reload }) {
  const [assignee, setAssignee] = useState(approval.eligible_assignees?.[0]?.id || '');
  const [error, setError] = useState('');
  if (!approval.can_assign_fulfillment || !approval.eligible_assignees?.length) return null;
  async function handleAssign(event) {
    event.preventDefault();
    try {
      await assignApproval(approval.id, Number(assignee));
      setError('');
      reload();
    } catch (err) {
      setError(err.message || 'Unable to assign request.');
    }
  }
  return (
    <form className="team-action-form" onSubmit={handleAssign}>
      <h3>Assign Fulfillment</h3>
      <label><span>Assignee</span><select value={assignee} onChange={(event) => setAssignee(event.target.value)}>{approval.eligible_assignees.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
      <button type="submit">Assign Work</button>
      {error && <p className="team-form-message is-error">{error}</p>}
    </form>
  );
}

export default function ApprovalDetailPage() {
  const { approvalId } = useParams();
  const [approval, setApproval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadApproval = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApproval(approvalId);
      setApproval(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load approval.');
    } finally {
      setLoading(false);
    }
  }, [approvalId]);

  useEffect(() => { loadApproval(); }, [loadApproval]);

  async function handleFulfillment(status) {
    await updateFulfillment(approval.id, status);
    loadApproval();
  }

  if (loading) return <div className="staff-shell"><ToolSidebar active="approvals" /><main className="staff-page internal-page approvals-page"><div className="team-detail-skeleton" /></main></div>;
  if (error || !approval) return <div className="staff-shell"><ToolSidebar active="approvals" /><main className="staff-page internal-page approvals-page"><section className="staff-card"><p className="staff-empty inline team-error">{error || 'Approval not found.'}</p></section></main></div>;

  return (
    <div className="staff-shell">
      <ToolSidebar active="approvals" />
      <main className="staff-page internal-page approvals-page">
        <header className="staff-header internal-hero team-detail-header">
          <Link to="/approvals" className="staff-strong-link">Back to Approvals</Link>
          <span>{approval.category_label}</span>
          <h1>{approval.title}</h1>
          <p>{approval.description}</p>
          <div className="team-detail-metrics">
            <DetailMetric label="Decision" value={approval.decision_status_label} />
            <DetailMetric label="Fulfillment" value={approval.fulfillment_status_label} />
            <DetailMetric label="Priority" value={approval.priority_label} />
            <DetailMetric label="Approver" value={approval.current_approver?.name || 'Approver queue'} />
          </div>
        </header>

        <section className="team-detail-grid">
          <section className="staff-card team-section">
            <div className="staff-card-header"><h2>Request</h2></div>
            <div className="team-section-body approval-detail-list">
              <DetailMetric label="Requested by" value={approval.requester?.name} />
              <DetailMetric label="Requesting team" value={approval.requesting_team?.name} />
              <DetailMetric label="Target team" value={approval.target_team?.name} />
              <DetailMetric label="Project" value={approval.project?.name} />
              <DetailMetric label="Type" value={approval.request_type_label} />
              <DetailMetric label="Submitted" value={new Date(approval.created_at).toLocaleString()} />
            </div>
          </section>

          <section className="staff-card team-section">
            <div className="staff-card-header"><h2>Actions</h2></div>
            <div className="team-section-body">
              {approval.can_decide && <>
                <ActionForm title="Approve" label="Decision note" placeholder="Optional approval note" onSubmit={async (note) => { await approveRequest(approval.id, note); loadApproval(); }} />
                <ActionForm title="Decline" label="Decline reason" placeholder="A reason is required" required onSubmit={async (reason) => { await declineRequest(approval.id, reason); loadApproval(); }} />
                <ActionForm title="Request More Information" label="Question/message" placeholder="What does the requester need to clarify?" required onSubmit={async (message) => { await requestMoreInfo(approval.id, message); loadApproval(); }} />
              </>}
              {approval.decision_status === 'info_requested' && <ActionForm title="Respond" label="Response" placeholder="Provide the requested information" required onSubmit={async (message) => { await respondToInfoRequest(approval.id, message); loadApproval(); }} />}
              <AssignmentForm approval={approval} reload={loadApproval} />
              {approval.assigned_to && <div className="approval-action-row"><button type="button" onClick={() => handleFulfillment('in_progress')}>Mark In Progress</button><button type="button" onClick={() => handleFulfillment('completed')}>Mark Completed</button></div>}
              {!approval.can_decide && approval.decision_status !== 'info_requested' && !approval.assigned_to && <p className="staff-empty inline">No actions are available for your role right now.</p>}
            </div>
          </section>
        </section>

        <section className="staff-card team-section">
          <div className="staff-card-header"><h2>Activity</h2></div>
          <div className="team-section-body approval-activity-list">
            {approval.activity?.length ? approval.activity.map((item) => <article className="team-note" key={item.id}><strong>{item.summary || item.action}</strong>{item.note && <p>{item.note}</p>}<small>{item.actor} · {new Date(item.created_at).toLocaleString()}</small></article>) : <p className="staff-empty inline">No activity yet.</p>}
          </div>
        </section>
      </main>
    </div>
  );
}